import { useCallback, useRef, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { InputOTP, InputOTPGroup, InputOTPSlot } from "@/components/ui/input-otp";

type Resolver = (value: boolean) => void;

function isVerifiedFactor(f: any): boolean {
  const status = String(f?.status ?? f?.factor_status ?? "").toLowerCase();
  if (status === "verified") return true;
  if (f?.verified === true) return true;
  return false;
}

function getFirstVerifiedTotpFactorId(factorsData: any): string | null {
  const totp = factorsData?.totp;
  if (Array.isArray(totp) && totp.length > 0) {
    const verified = totp.find((x: any) => isVerifiedFactor(x));
    return verified?.id || null;
  }
  const all = factorsData?.all;
  if (Array.isArray(all)) {
    const f = all.find((x: any) => (x?.factor_type === "totp" || x?.type === "totp") && isVerifiedFactor(x));
    return f?.id || null;
  }
  return null;
}

// Step-up "freshness": even when the session is already AAL2 (user signed in
// with MFA), require a fresh TOTP code for sensitive actions if the last
// verification was older than this window (5 min). See T54/T55.
const STEP_UP_FRESHNESS_MS = 5 * 60 * 1000;
const STEP_UP_FRESHNESS_KEY = "halomora.mfa.lastStepUpAt";
function readLastStepUpAt(): number {
  try {
    const v = typeof sessionStorage !== "undefined" ? sessionStorage.getItem(STEP_UP_FRESHNESS_KEY) : null;
    const n = v ? Number(v) : 0;
    return Number.isFinite(n) ? n : 0;
  } catch { return 0; }
}
function writeLastStepUpAt(ts: number) {
  try { if (typeof sessionStorage !== "undefined") sessionStorage.setItem(STEP_UP_FRESHNESS_KEY, String(ts)); } catch { /* noop */ }
}

export function useMfaStepUp() {
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string>("");
  const [code, setCode] = useState<string>("");
  const [challengeId, setChallengeId] = useState<string | null>(null);
  const [factorId, setFactorId] = useState<string | null>(null);

  const resolverRef = useRef<Resolver | null>(null);

  const closeAndResolve = useCallback((val: boolean) => {
    try {
      resolverRef.current?.(val);
    } finally {
      resolverRef.current = null;
      setOpen(false);
      setLoading(false);
      setError("");
      setCode("");
      setChallengeId(null);
      setFactorId(null);
    }
  }, []);

  const beginChallenge = useCallback(async () => {
    setLoading(true);
    setError("");
    setCode("");
    try {
      const mfa = (supabase as any)?.auth?.mfa;
      if (!mfa) throw new Error("MFA not available");

      const { data: factorsData, error: listErr } = await mfa.listFactors();
      if (listErr) throw listErr;
      const fid = getFirstVerifiedTotpFactorId(factorsData);
      if (!fid) throw new Error("2FA is not enabled for this account");
      setFactorId(fid);

      const { data: ch, error: chErr } = await mfa.challenge({ factorId: fid });
      if (chErr) throw chErr;
      setChallengeId(ch?.id || null);
      if (!ch?.id) throw new Error("Missing challenge id");
    } catch (e: any) {
      setError(e?.message || "Failed to start verification");
    } finally {
      setLoading(false);
    }
  }, []);

  const ensureAAL2 = useCallback(async () => {
    const { data: { session } } = await supabase.auth.getSession();
    if (!session) return false;

    const mfa = (supabase as any)?.auth?.mfa;
    if (!mfa) return true; // MFA not supported -> allow

    // If user has no factor enrolled, don't block (since step-up is only meaningful if enabled)
    const { data: factorsData, error: listErr } = await mfa.listFactors();
    if (listErr) return true;
    const fid = getFirstVerifiedTotpFactorId(factorsData);
    if (!fid) return true;

    // Even if the session is AAL2, require a fresh step-up if the last one was
    // performed more than STEP_UP_FRESHNESS_MS ago. Catches T54/T55.
    try {
      const { data } = await mfa.getAuthenticatorAssuranceLevel();
      const lvl = data?.currentLevel || data?.current_level;
      if (lvl === "aal2") {
        const last = readLastStepUpAt();
        if (last && Date.now() - last < STEP_UP_FRESHNESS_MS) return true;
      }
    } catch {
      // ignore and fall back to prompting
    }

    // Prompt user for TOTP
    return await new Promise<boolean>((resolve) => {
      resolverRef.current = resolve;
      setOpen(true);
      // kick off challenge
      void beginChallenge();
    });
  }, [beginChallenge]);

  const verify = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const mfa = (supabase as any)?.auth?.mfa;
      if (!mfa) throw new Error("MFA not available");
      const fid = factorId;
      const cid = challengeId;
      if (!fid || !cid) throw new Error("Missing challenge");
      const otp = (code || "").replace(/\s/g, "");
      if (otp.length !== 6) throw new Error("Enter a 6-digit code");
      const { error: verErr } = await mfa.verify({ factorId: fid, challengeId: cid, code: otp });
      if (verErr) throw verErr;
      writeLastStepUpAt(Date.now());
      closeAndResolve(true);
    } catch (e: any) {
      setError(e?.message || "Invalid code");
    } finally {
      setLoading(false);
    }
  }, [challengeId, closeAndResolve, code, factorId]);

  const StepUpDialog = (
    <Dialog open={open} onOpenChange={(v) => { if (!v) closeAndResolve(false); }}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Verify it’s you</DialogTitle>
          <DialogDescription>
            Enter the 6‑digit code from your authenticator app to continue.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          {error && <div className="text-sm text-red-600 border border-red-200 bg-red-50 rounded p-2">{error}</div>}
          <InputOTP maxLength={6} value={code} onChange={setCode}>
            <InputOTPGroup>
              <InputOTPSlot index={0} />
              <InputOTPSlot index={1} />
              <InputOTPSlot index={2} />
              <InputOTPSlot index={3} />
              <InputOTPSlot index={4} />
              <InputOTPSlot index={5} />
            </InputOTPGroup>
          </InputOTP>
          <div className="flex gap-2 justify-end">
            <Button variant="outline" disabled={loading} onClick={() => closeAndResolve(false)}>Cancel</Button>
            <Button disabled={loading || (code || "").replace(/\s/g, "").length !== 6} onClick={verify}>
              {loading ? "Verifying…" : "Continue"}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );

  return { ensureAAL2, StepUpDialog };
}

